import { useNavigate, useParams } from 'react-router-dom'
import { LocationSweepstakeDetailsHeader } from '../LocationSweepstakeDetailsHeader'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { compact, isNil } from 'lodash'
import { Box, Button, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useSweepstakeParticipatingLocationsAndEnrollmentAgreement } from 'stores/useSweepstakeParticipatingLocationsAndEnrollmentAgreement'
import { ParticipatingLocationList } from './ParticipatingLocationList'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { useApiQuery } from 'hooks/useApiQuery'
import { useGetContestLicensedEstablishment } from 'hooks/api/useGetContestLicensedEstablishment'
import { type LicensedEstablishment } from 'src/types/api'
import { useGetLicensedEstablishmentByCorporateAccount } from 'hooks/api/useGetLicensedEstablishmentByCorporateAccount'
import { useGetParticipatingLocations } from 'hooks/useGetParticipatingLocations'
import { getLEIdsByPermission } from 'utils/permissions'
import { useSetMe } from 'stores/useMeQuery'

export const LocationSweepstakeParticipatingLocationsPage = () => {
  const navigate = useNavigate()
  const { id: idParam } = useParams()
  const sweepstakeId = Number(idParam)
  const { currentCorporateAccountId, currentCorporateAccountName } =
    useCurrentCorporateAccountStore()

  const sweepstakeQuery = useApiQuery({
    path: '/le-connect/contests/{id}',
    method: 'get',
    queryKey: [
      '/le-connect/contests/{id}',
      sweepstakeId,
      currentCorporateAccountId,
    ],
    queryArgs: { id: sweepstakeId },
    enabled: sweepstakeId != null,
  })

  const leByCorporateAccountQuery =
    useGetLicensedEstablishmentByCorporateAccount({
      corporateAccountId: Number(currentCorporateAccountId),
    })

  const licensedEstablishmentSearchQuery = useGetContestLicensedEstablishment({
    contestId: sweepstakeId,
  })

  const { setParticipatingLocations, setEnrollmentDetails } =
    useSweepstakeParticipatingLocationsAndEnrollmentAgreement()

  const sweepstake = sweepstakeQuery.data
  const contestLes = licensedEstablishmentSearchQuery.data

  const {
    corporateAccounts,
    licensedEstablishments,
    organizations,
    isPending,
  } = useGetParticipatingLocations({ contestLes, currentCorporateAccountId })

  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'enrollInSweepstakesPermission'
  )

  // Filter data by permission
  const licensedEstablishmentsFiltered = licensedEstablishments?.filter(
    (x) =>
      leIdsByPermission.includes(Number(x.id)) && !x.excludedFromSweepstakes
  )

  // Filter arrays based on selectedCorporateAccountId (the CA selected in the dropdown at the top)
  const filteredCorporateAccounts = corporateAccounts?.filter(
    (le) => le.id === selectedCorporateAccountId
  )

  const filteredLicensedEstablishments: typeof licensedEstablishments = []
  licensedEstablishmentsFiltered?.forEach((le) => {
    if (le?.corporateAccountId === selectedCorporateAccountId) {
      filteredLicensedEstablishments.push(le)
    }
  })

  const filteredOrganizations: typeof organizations = []
  organizations?.forEach((org) => {
    if (org?.corporateAccountId === selectedCorporateAccountId) {
      filteredOrganizations.push(org)
    }
  })

  const availableLesInaCa = useMemo(() => {
    return compact(
      (leByCorporateAccountQuery.data ?? []).filter(
        (x) => x.corporateAccountId === currentCorporateAccountId
      )
    )
  }, [leByCorporateAccountQuery.data])

  const buttonMessage = useMemo(() => {
    if (
      licensedEstablishments !== undefined &&
      availableLesInaCa !== undefined
    ) {
      const currentlyEnrolledLesLength = licensedEstablishments.length
      const availableLesLength = availableLesInaCa.length

      if (currentlyEnrolledLesLength === availableLesLength) {
        return 'All My Locations Enrolled'
      }
      if (
        currentlyEnrolledLesLength < availableLesLength &&
        currentlyEnrolledLesLength > 0
      ) {
        return 'Enroll Additional Locations'
      }

      return 'Enroll in Sweepstakes'
    }
  }, [availableLesInaCa, licensedEstablishments])

  if (
    sweepstakeQuery.isPending ||
    licensedEstablishmentSearchQuery.isPending ||
    leByCorporateAccountQuery.isPending ||
    isPending
  ) {
    return <ActivityIndicator />
  }

  if (
    sweepstakeQuery.isError ||
    licensedEstablishmentSearchQuery.isError ||
    isNil(sweepstake) ||
    leByCorporateAccountQuery.isError
  ) {
    return <Box>An error occurred fetching the sweepstake.</Box>
  }

  return (
    <LocationSweepstakeDetailsHeader
      sweepstake={sweepstake}
      currentTab={`/LocationSweepstakes/${sweepstake?.id}/ParticipatingLocations`}
      actionButton={
        sweepstake.sweepstakeData?.state === 'Enrollable' && (
          <Button
            sx={{ mr: 1 }}
            onClick={async () => {
              setParticipatingLocations(undefined)
              setEnrollmentDetails(undefined)
              await navigate(
                `/Sweepstakes/${sweepstakeQuery?.data?.id}/EnrollLocations`
              )
            }}
            variant="contained"
            disabled={buttonMessage === 'All My Locations Enrolled'}
          >
            {buttonMessage}
          </Button>
        )
      }
    >
      <Typography variant="h2">
        My Participating {currentCorporateAccountName} Locations
      </Typography>

      {sweepstakeQuery.isPending ? (
        <ActivityIndicator marginTop={8} />
      ) : (
        <ParticipatingLocationList
          licensedEstablishments={
            filteredLicensedEstablishments as LicensedEstablishment[]
          }
          corporateAccounts={filteredCorporateAccounts}
          organizations={filteredOrganizations}
        />
      )}
      {(sweepstakeQuery.isError || isNil(sweepstake)) ?? (
        <Box>An error occurred fetching the sweepstake.</Box>
      )}
    </LocationSweepstakeDetailsHeader>
  )
}
